import { lazy } from "react";
import { createRoute } from "@tanstack/react-router";
import { Route as authenticatedRoute } from "./_authenticated";
import { authGuard } from "./guards";

const dashboard = {
  analysis: lazy(() => import("../views/dashboard/DashboardAnalysis")),
  analytics: lazy(() => import("../views/dashboard/DashboardAnalytics")),
};

const services = {
  news: lazy(() => import("../views/News/News")),
  newsAdd: lazy(() => import("../views/News/NewsAdd")),
  price: lazy(() => import("../views/Price/Price")),
  priceAdd: lazy(() => import("../views/Price/PriceAdd")),
  priceEditor: lazy(() => import("../views/Price/PriceEditor")),
  publication: lazy(() => import("../views/Publication/Publication")),
  publicationAdd: lazy(() => import("../views/Publication/PublicationAdd")),
  partners: lazy(() => import("../views/Partners/Partners")),
  partnersAdd: lazy(() => import("../views/Partners/PartnersAdd")),
  faq: lazy(() => import("../views/FAQ/FAQ")),
  faqAdd: lazy(() => import("../views/FAQ/FAQAdd")),
  tools: lazy(() => import("../views/Tools/Tools")),
  toolsAdd: lazy(() => import("../views/Tools/ToolsAdd")),
  video: lazy(() => import("../views/Video/Video")),
  videoAdd: lazy(() => import("../views/Video/VideoAdd")),
  about: lazy(() => import("../views/About/About")),
  contacts: lazy(() => import("../views/Contacts/Contacts")),
  termsAndConditions: lazy(() =>
    import("../views/Settings/TermsAndConditions")
  ),
  privacyPolicy: lazy(() => import("../views/Settings/PrivacyPolicy")),
  farmers: lazy(() => import("../views/Farmers/Farmers")),
  customers: lazy(() => import("../views/Customers/Customers")),
  customerDetail: lazy(() => import("../views/Customers/CustomerDetail")),
  vendors: lazy(() => import("../views/Vendors/Vendors")),
  users: lazy(() => import("../views/Users/Users")),
  upsertUser: lazy(() => import("../views/Users/UserDetail")),
  roles: lazy(() => import("../views/Users/Roles")),
  SendSms: lazy(() => import("../views/Sms/SendSms")),
  SendEmail: lazy(() => import("../views/Email/SendEmail")),
  SendPushNotification: lazy(() => import("../views/PushNotification/SendPushNotification")),
  SocialMedia: lazy(() => import("../views/Settings/SocialMedia")),
  registerFarmerForm: lazy(() => import("../views/Forms/RegisterForm")),
  farmerView: lazy(() => import("../views/Farmers/FarmerDetail")),
  products: lazy(() => import("../views/Products/Products")),
  productEditor: lazy(() => import("../views/Products/ProductEditor")),
  productDetail: lazy(() => import("../views/Products/ProductDetail")),
  categories: lazy(() => import("../views/Products/Categories/Categories")),
  categoryDetail: lazy(() =>
    import("../views/Products/Categories/CategoryDetail")
  ),
  reviews: lazy(() => import("../views/Products/Reviews/Reviews")),
  promotions: lazy(() => import("../views/Promotions/Promotions")),
  promotionPlans: lazy(() => import("../views/Promotions/Plans")),
  promotionPlanForm: lazy(() => import("../views/Promotions/PlanForm")),
  accountSettings: lazy(() => import("../views/Account/AccountSettings")),
};

export const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/dashboard",
  component: dashboard.analysis,
});

export const analyticsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/analytics",
  component: dashboard.analytics,
});

export const mgtRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/mgt",
  beforeLoad: authGuard({
    roles: [
      "viewusers",
      "viewfarmers",
      "viewcustomers",
      "viewvendors",
      "adduser",
      "edituser",
      "addfarmers",
      "editfarmers",
      "viewadmins",
      "addadmins",
      "editadmins",
      "viewroles",
    ],
  }),
});

export const adminUsersRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/admin-users",
  beforeLoad: authGuard({ roles: ["viewadmins"] }),
  component: services.users,
});

export const adminUsersAddRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/admin-users/add",
  beforeLoad: authGuard({ roles: ["addadmins"] }),
  component: services.upsertUser,
});

export const adminUsersDetailRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/admin-users/$adminId",
  beforeLoad: authGuard({ roles: ["editadmins"] }),
  loader: async ({ params }) => {
    return { adminId: params.adminId };
  },
  component: AdminDetail,
});

function AdminDetail() {
  const { adminId } = adminUsersDetailRoute.useParams();
  return <services.upsertUser id={adminId} />;
}

export const rolesRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/roles",
  beforeLoad: authGuard({ roles: ["viewroles"] }),
  component: services.roles,
});

export const farmersRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/farmers",
  beforeLoad: authGuard({ roles: ["viewfarmers"] }),
  component: services.farmers,
});

export const farmersAddRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/farmers/add",
  beforeLoad: authGuard({ roles: ["addfarmers"] }),
  component: services.registerFarmerForm,
});

export const farmersViewRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/farmers/$id",
  beforeLoad: authGuard({ roles: ["viewfarmers"] }),
  loader: async ({ params }) => {
    return { id: params.id };
  },
  component: FarmerView,
});

function FarmerView() {
  return <services.farmerView />;
}

export const farmersDetailRoute = farmersViewRoute; // backwards-compat alias

export const farmersEditRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/farmers/$id/edit",
  beforeLoad: authGuard({ roles: ["editfarmers"] }),
  loader: async ({ params }) => {
    return { id: params.id };
  },
  component: FarmerEdit,
});

function FarmerEdit() {
  const { id } = farmersEditRoute.useParams();
  return <services.registerFarmerForm id={id} />;
}

export const customersRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/customers",
  beforeLoad: authGuard({ roles: ["viewcustomers"] }),
  component: services.customers,
});

export const customerDetailRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/customers/$id",
  beforeLoad: authGuard({ roles: ["viewcustomers"] }),
  loader: async ({ params }) => ({ id: params.id }),
  component: services.customerDetail,
});

export const vendorsRoute = createRoute({
  getParentRoute: () => mgtRoute,
  path: "/vendors",
  beforeLoad: authGuard({ roles: ["viewvendors"] }),
  component: services.vendors,
});

export const commRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/comm",
  beforeLoad: authGuard({
    roles: ["sendnotifications"],
  }),
});

export const smsRoute = createRoute({
  getParentRoute: () => commRoute,
  path: "/sms",
  beforeLoad: authGuard({ roles: ["sendnotifications"] }),
  component: services.SendSms,
});

export const emailRoute = createRoute({
  getParentRoute: () => commRoute,
  path: "/email",
  beforeLoad: authGuard({ roles: ["sendnotifications"] }),
  component: services.SendEmail,
});

export const pushNotificationRoute = createRoute({
  getParentRoute: () => commRoute,
  path: "/push-notification",
  beforeLoad: authGuard({ roles: ["sendnotifications"] }),
  component: services.SendPushNotification,
});

export const priceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/price",
  beforeLoad: authGuard({ roles: ["viewprices", "addprices", "editprices"] }),
});

export const priceListRoute = createRoute({
  getParentRoute: () => priceRoute,
  path: "/price",
  component: services.price,
});

export const priceAddRoute = createRoute({
  getParentRoute: () => priceRoute,
  path: "/price/add",
  beforeLoad: authGuard({ roles: ["addprices"] }),
  component: services.priceAdd,
});

export const priceDetailRoute = createRoute({
  getParentRoute: () => priceRoute,
  path: "/price/detail",
  beforeLoad: authGuard({ roles: ["editprices"] }),
  component: services.priceEditor,
});

export const contentRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/content",
  beforeLoad: authGuard({
    roles: [
      "viewnews",
      "addnews",
      "editnews",
      "viewpublications",
      "addpublications",
      "editpublications",
      "viewtools",
      "addtools",
      "edittools",
      "viewvideos",
      "addvideos",
      "editvideos",
      "viewpartners",
      "addpartners",
      "editpartners",
      "viewfaqs",
      "addfaqs",
      "editfaqs",
    ],
  }),
});

export const newsRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/news",
  beforeLoad: authGuard({ roles: ["viewnews"] }),
  component: services.news,
});

export const newsAddRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/news/add",
  beforeLoad: authGuard({ roles: ["addnews"] }),
  component: services.newsAdd,
});

export const newsDetailRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/news/$id",
  beforeLoad: authGuard({ roles: ["editnews"] }),
  component: NewsDetail,
});

function NewsDetail() {
  const { id } = newsDetailRoute.useParams();
  return <services.newsAdd id={id} />;
}

export const partnersRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/partners",
  beforeLoad: authGuard({ roles: ["viewpartners"] }),
  component: services.partners,
});

export const partnersAddRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/partners/add",
  beforeLoad: authGuard({ roles: ["addpartners"] }),
  component: services.partnersAdd,
});

export const partnersDetailRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/partners/$id",
  beforeLoad: authGuard({ roles: ["editpartners"] }),
  component: PartnersDetail,
});

function PartnersDetail() {
  const { id } = partnersDetailRoute.useParams();
  return <services.partnersAdd id={id} />;
}

export const publicationRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/publication",
  beforeLoad: authGuard({ roles: ["viewpublications"] }),
  component: services.publication,
});

export const publicationAddRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/publication/add",
  beforeLoad: authGuard({ roles: ["addpublications"] }),
  component: services.publicationAdd,
});

export const publicationDetailRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/publication/$id",
  beforeLoad: authGuard({ roles: ["editpublications"] }),
  component: PublicationDetail,
});

function PublicationDetail() {
  const { id } = publicationDetailRoute.useParams();
  return <services.publicationAdd id={id} />;
}

export const toolsRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/tools",
  beforeLoad: authGuard({ roles: ["viewtools"] }),
  component: services.tools,
});

export const toolsAddRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/tools/add",
  beforeLoad: authGuard({ roles: ["addtools"] }),
  component: services.toolsAdd,
});

export const toolsDetailRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/tools/$id",
  beforeLoad: authGuard({ roles: ["edittools"] }),
  component: ToolsDetail,
});

function ToolsDetail() {
  const { id } = toolsDetailRoute.useParams();
  return <services.toolsAdd id={id} />;
}

export const videoRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/video",
  beforeLoad: authGuard({ roles: ["viewvideos"] }),
  component: services.video,
});

export const videoAddRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/video/add",
  beforeLoad: authGuard({ roles: ["addvideos"] }),
  component: services.videoAdd,
});

export const videoDetailRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/video/$id",
  beforeLoad: authGuard({ roles: ["editvideos"] }),
  component: VideoDetail,
});

function VideoDetail() {
  const { id } = videoDetailRoute.useParams();
  return <services.videoAdd id={id} />;
}

export const faqRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/faq",
  component: services.faq,
  beforeLoad: authGuard({ roles: ["viewfaqs"] }),
});

export const faqAddRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/faq/add",
  component: services.faqAdd,
  beforeLoad: authGuard({ roles: ["addfaqs"] }),
});

export const faqDetailRoute = createRoute({
  getParentRoute: () => contentRoute,
  path: "/faq/$id",
  beforeLoad: authGuard({ roles: ["editfaqs"] }),
  component: FAQDetail,
});

function FAQDetail() {
  const { id } = faqDetailRoute.useParams();
  return <services.faqAdd id={id} />;
}

export const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/settings",
  beforeLoad: authGuard({ roles: ["managesettings"] }),
});

export const socialMediaRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/social-media",
  component: services.SocialMedia,
});

export const aboutRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/about",
  component: services.about,
});

export const contactRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/contact",
  component: services.contacts,
});

export const termsAndConditionsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/terms-and-conditions",
  component: services.termsAndConditions,
});

export const privacyPolicyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/privacy-policy",
  component: services.privacyPolicy,
});

export const productsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/products",
  beforeLoad: authGuard({
    roles: [
      "viewproducts",
      "editproducts",
      "managecategories",
      "managereviews",
      "viewpromotions",
      "viewpromotionplans",
    ],
  }),
});

export const productsListRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: "/",
  component: services.products,
});

export const productEditRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: "/edit/$id",
  beforeLoad: authGuard({ roles: ["editproducts"] }),
  component: services.productEditor,
});

export const productDetailRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: "/detail/$id",
  beforeLoad: authGuard({ roles: ["viewproducts"] }),
  component: services.productDetail,
});

export const categoriesRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: "/categories",
  beforeLoad: authGuard({ roles: ["managecategories"] }),
  component: services.categories,
});

export const categoryDetailRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: "/category/$categoryId",
  beforeLoad: authGuard({ roles: ["managecategories"] }),
  component: services.categoryDetail,
});

export const reviewsRoute = createRoute({
  getParentRoute: () => productsRoute,
  path: "/reviews",
  beforeLoad: authGuard({ roles: ["managereviews"] }),
  component: services.reviews,
});

export const promotionsRoute = createRoute({
  getParentRoute: () => productsRoute,
  beforeLoad: authGuard({ roles: ["viewpromotions"] }),
  path: "/promotions",
  component: services.promotions,
});

export const promotionPlansRoute = createRoute({
  getParentRoute: () => productsRoute,
  beforeLoad: authGuard({ roles: ["viewpromotionplans"] }),
  path: "/promotion-plans",
  component: services.promotionPlans,
});

export const promotionPlanAddRoute = createRoute({
  getParentRoute: () => productsRoute,
  beforeLoad: authGuard({ roles: ["addpromotionplans"] }),
  path: "/promotion-plans/add",
  component: services.promotionPlanForm,
});

export const promotionPlanDetailRoute = createRoute({
  getParentRoute: () => productsRoute,
  beforeLoad: authGuard({ roles: ["editpromotionplans"] }),
  path: "/promotion-plans/$id",
  loader: async ({ params }) => ({ id: params.id }),
  component: PromotionPlanDetail,
});

function PromotionPlanDetail() {
  const { id } = promotionPlanDetailRoute.useParams();
  return <services.promotionPlanForm id={id} />;
}

export const accountSettingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/account/settings",
  component: services.accountSettings,
});
